/**
 * PublicRestaurantSurvey — /restauracja/:location
 * Premium ankieta dla gości w restauracji + upload zdjęcia + miesięczne losowanie vouchera.
 * Reuses mjds-* style z PublicDeliverySurvey by zachować estetykę MR JERRY.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Star, Check, ChevronDown, Camera, Trash2, ArrowRight } from 'lucide-react';
import LanguageSwitch from './LanguageSwitch';
import { getLang, setLang, useT } from './surveyI18n';
import './PublicDeliverySurvey.css'; // ten sam look-and-feel
import PublicFooter from '@/components/legal/PublicFooter';
import LegalIconMenu from '@/components/legal/LegalIconMenu';

const API = process.env.REACT_APP_BACKEND_URL;
const DEVICE_KEY = 'mrj_rest_device_id';

function getOrCreateDeviceId() {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now());
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

async function compressImage(file, maxSize = 1200, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        const k = width > height ? maxSize / width : maxSize / height;
        width = Math.round(width * k);
        height = Math.round(height * k);
      }
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const Page = ({ children, lang, onLang }) => (
  <div className="mjds-root">
    <LanguageSwitch value={lang} onChange={onLang} />
    <div className="mjds-shell">{children}</div>
    <PublicFooter />
    <LegalIconMenu position="bottom-right" />
  </div>
);

export default function PublicRestaurantSurvey() {
  const { location } = useParams();
  const [lang, setLangState] = useState(getLang());
  const t = useT(lang);
  const changeLang = (l) => { setLang(l); setLangState(l); };
  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState({});
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [showContact, setShowContact] = useState(false);
  const [photo, setPhoto] = useState(''); // data URL
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/public-restaurant-survey/${location}`)
      .then((r) => setConfig(r.data))
      .catch((e) => setError(e?.response?.data?.detail || t.ups));
  }, [location]);

  const requiredQuestions = useMemo(() =>
    config ? config.questions.filter((q) => q.type !== 'text') : []
  , [config]);
  const answeredRequired = useMemo(() =>
    requiredQuestions.filter((q) => answers[q.id] !== undefined && answers[q.id] !== null && answers[q.id] !== '').length
  , [requiredQuestions, answers]);
  const totalRequired = requiredQuestions.length + 1; // +1 za wymagane zdjęcie
  const answeredAll = answeredRequired + (photo ? 1 : 0);
  const progressPct = totalRequired === 0 ? 0 : Math.round((answeredAll / totalRequired) * 100);
  const canSubmit = answeredRequired === requiredQuestions.length && !!photo && !submitting;

  const setAns = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));
  const setCmt = (id, v) => setComments((c) => ({ ...c, [id]: v }));

  const onPhotoSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { setError(lang === 'en' ? 'File must be an image' : 'Plik musi być zdjęciem'); return; }
    try {
      const dataUrl = await compressImage(f);
      setPhoto(dataUrl);
    } catch { setError(lang === 'en' ? 'Could not process the photo' : 'Nie udało się przetworzyć zdjęcia'); }
  };

  const submit = async () => {
    if (!config || !canSubmit) return;
    const ansList = config.questions.map((q) => {
      const v = answers[q.id];
      if (v === undefined || v === null || v === '') return null;
      let valueLabel = '';
      if (q.type === 'choice' && Array.isArray(q.options)) {
        const opt = q.options.find((o) => o.value === v);
        valueLabel = opt ? `${opt.emoji ? opt.emoji + ' ' : ''}${opt.label}` : v;
      }
      const comment = q.allowComment ? (comments[q.id] || '').trim() : '';
      return { id: q.id, type: q.type, label: q.label, value: v, valueLabel, comment };
    }).filter(Boolean);

    setSubmitting(true);
    const fd = new FormData();
    fd.append('deviceId', getOrCreateDeviceId());
    fd.append('answers', JSON.stringify(ansList));
    fd.append('contact_name', contactName);
    fd.append('contact_phone', contactPhone);
    fd.append('contact_email', contactEmail);
    if (photo) fd.append('photo', photo);
    try {
      const r = await axios.post(`${API}/api/public-restaurant-survey/${location}/submit`, fd);
      setResult(r.data);
      axios.post(`${API}/api/legal/consents`, {
        context: 'form:restaurant-survey',
        documentTypes: ['regulamin', 'privacy'],
        userEmail: contactEmail || null,
        userPhone: contactPhone || null,
      }).catch(() => {});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(e?.response?.data?.detail || (lang === 'en' ? 'Something went wrong' : 'Coś poszło nie tak'));
    } finally { setSubmitting(false); }
  };

  if (error && !result && !config) return (
    <Page lang={lang} onLang={changeLang}>
      <div className="mjds-brand">MR JERRY</div>
      <div className="mjds-error" data-testid="public-rest-error">
        <div className="icon">🚫</div>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{t.ups}</div>
        <div style={{ fontSize: 13 }}>{error}</div>
      </div>
    </Page>
  );

  if (!config && !result) return (
    <Page lang={lang} onLang={changeLang}>
      <div className="mjds-brand">MR JERRY</div>
      <div className="mjds-loading"><div><span className="dot"/><span className="dot"/><span className="dot"/></div>
        <div style={{ marginTop: 12 }}>{t.loading}</div>
      </div>
    </Page>
  );

  if (result) return (
    <Page lang={lang} onLang={changeLang}>
      <div className="mjds-brand">MR JERRY</div>
      <div className="mjds-success" data-testid="public-rest-success">
        <div className="mjds-celebrate">🎉</div>
        <div className="mjds-thanks">{t.thanks}</div>
        <div className="mjds-reward-title" data-testid="public-rest-voucher-title">
          🎁 {result.voucher_title || t.voucher_default_title}
        </div>
        <div className="mjds-reward-desc" data-testid="public-rest-voucher-description">
          {result.voucher_description}
        </div>
        <div style={{ padding: 14, background: 'rgba(212,175,55,0.08)', borderRadius: 12, border: '1px dashed #d4af37', margin: '12px 0' }}>
          <div style={{ color: '#d4af37', fontWeight: 900, fontSize: 22 }} data-testid="public-rest-voucher-amount">
            🏆 {result.voucher_amount} zł
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted, #94a3b8)', marginTop: 4 }}>
            {t.pool_size(result.month, result.your_pool_size)}
          </div>
        </div>
        <div className="mjds-footer">{t.good_luck_restaurant} <span>@mr_jerry</span></div>
        <div style={{ marginTop: 14, textAlign: 'center', fontSize: 11, color: 'var(--text-muted, #94a3b8)' }}>
          Udział w losowaniu odbywa się zgodnie z{' '}
          <a href="/regulamin-konkursu" target="_blank" rel="noreferrer" style={{ color: '#d4af37', textDecoration: 'none', fontWeight: 700 }}>
            Regulaminem konkursu
          </a>.
        </div>
      </div>
    </Page>
  );

  return (
    <Page lang={lang} onLang={changeLang}>
      <div className="mjds-brand">MR JERRY</div>
      <div className="mjds-hero"><span className="emoji">🍽️</span>{t.hero_restaurant}</div>
      <p className="mjds-subhero">{t.brand_sub_restaurant}</p>

      <div className="mjds-progress-wrap">
        <div className="mjds-progress-meta">
          <span>{t.progress}</span>
          <span data-testid="public-rest-progress">{answeredAll} / {totalRequired}</span>
        </div>
        <div className="mjds-progress-bar"><div className="mjds-progress-fill" style={{ width: `${progressPct}%` }} /></div>
      </div>

      {config.questions.map((q, idx) => (
        <div className="mjds-card" key={q.id} style={{ animationDelay: `${idx * 60}ms` }}>
          <div className="mjds-q-idx">{t.question_label} {idx + 1}{q.type === 'text' ? ` · ${t.optional_badge}` : ''}</div>
          <div className="mjds-q-label">{q.label}</div>
          {q.hint && <div className="mjds-q-hint">{q.hint}</div>}

          {q.type === 'rating' && (
            <div className="mjds-stars">
              {[1,2,3,4,5].map((n) => {
                const selected = answers[q.id] === n;
                const filled = answers[q.id] && n <= answers[q.id];
                return (
                  <button key={n} type="button" onClick={() => setAns(q.id, n)}
                    className={`mjds-star-btn ${selected ? 'is-selected' : ''} ${filled ? 'is-filled' : ''}`}
                    data-testid={`q-${q.id}-${n}`}>
                    <Star size={22} /><span className="mjds-star-num">{n}</span>
                  </button>
                );
              })}
            </div>
          )}
          {q.type === 'choice' && Array.isArray(q.options) && (
            <div className="mjds-choices">
              {q.options.map((opt) => {
                const sel = answers[q.id] === opt.value;
                const tone = opt.tone || 'neutral';
                return (
                  <button key={opt.value} type="button" onClick={() => setAns(q.id, opt.value)}
                    className={`mjds-choice-btn tone-${tone} ${sel ? 'is-selected' : ''}`}
                    data-testid={`q-${q.id}-${opt.value}`}>
                    {opt.emoji && <span className="emoji">{opt.emoji}</span>}
                    <span className="label">{opt.label}</span>
                    <span className="check"><Check size={14} strokeWidth={3} /></span>
                  </button>
                );
              })}
            </div>
          )}
          {q.type === 'text' && (
            <textarea className="mjds-textarea"
              value={answers[q.id] || ''} onChange={(e) => setAns(q.id, e.target.value)}
              placeholder={t.text_placeholder} maxLength={1000}
              data-testid={`q-${q.id}-text`} />
          )}
          {q.allowComment && q.type !== 'text' && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted, #94a3b8)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                {t.add_comment}
              </div>
              <textarea className="mjds-textarea"
                value={comments[q.id] || ''} onChange={(e) => setCmt(q.id, e.target.value)}
                placeholder={t.comment_placeholder} maxLength={500} rows={2} style={{ minHeight: 60 }}
                data-testid={`q-${q.id}-comment`} />
            </div>
          )}
        </div>
      ))}

      {/* Photo upload — WYMAGANE */}
      <div className="mjds-card" style={!photo ? { borderColor: 'rgba(212,175,55,0.5)' } : undefined}>
        <div className="mjds-q-idx">{t.photo_required}</div>
        <div className="mjds-q-label">{t.photo_label}</div>
        <div className="mjds-q-hint">{t.photo_hint}</div>
        {photo ? (
          <div style={{ marginTop: 12, position: 'relative' }}>
            <img src={photo} alt={t.photo_label} data-testid="public-rest-photo-preview"
              style={{ width: '100%', maxHeight: 280, objectFit: 'cover', borderRadius: 12, border: '1px solid var(--gold-dim, rgba(212,175,55,0.32))' }} />
            <button onClick={() => setPhoto('')}
              data-testid="public-rest-photo-remove"
              style={{ position: 'absolute', top: 8, right: 8, padding: 8, borderRadius: 999, border: 'none',
                background: 'rgba(239,68,68,0.9)', color: '#fff', cursor: 'pointer' }}><Trash2 size={14} /></button>
          </div>
        ) : (
          <label htmlFor="rest-photo-input" data-testid="public-rest-photo-label" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            marginTop: 12, padding: 20, borderRadius: 12,
            border: '2px dashed rgba(212,175,55,0.4)',
            background: 'rgba(212,175,55,0.05)',
            cursor: 'pointer', color: '#d4af37', fontWeight: 700, fontSize: 14,
          }}>
            <Camera size={20} /> {t.choose_photo}
            <input id="rest-photo-input" type="file" accept="image/*" capture="environment"
              data-testid="public-rest-photo-input"
              onChange={onPhotoSelect} style={{ display: 'none' }} />
          </label>
        )}
      </div>

      {/* Contact */}
      <div className="mjds-card">
        <button type="button" className="mjds-contact-toggle" onClick={() => setShowContact((v) => !v)}
          data-testid="public-rest-contact-toggle" style={{ width: '100%' }}>
          📞&nbsp; {showContact ? t.contact_hide : t.contact_show_restaurant}
          <ChevronDown size={16} style={{ marginLeft: 'auto', transition: 'transform 220ms', transform: showContact ? 'rotate(180deg)' : 'none' }} />
        </button>
        {showContact && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted, #94a3b8)', marginBottom: 6 }}>
              {t.contact_hint_restaurant}
            </div>
            <input className="mjds-input" value={contactName} onChange={(e) => setContactName(e.target.value)}
              placeholder={t.name} data-testid="public-rest-contact-name" />
            <input className="mjds-input" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)}
              placeholder={t.phone} inputMode="tel" data-testid="public-rest-contact-phone" />
            <input className="mjds-input" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
              placeholder={t.email} inputMode="email" data-testid="public-rest-contact-email" />
          </div>
        )}
      </div>

      {error && <div className="mjds-error" style={{ marginBottom: 14 }} data-testid="public-rest-submit-error">{error}</div>}

      <button className="mjds-cta" onClick={submit} disabled={!canSubmit} data-testid="submit-rest-survey">
        {submitting ? t.submitting
          : canSubmit ? <>{t.submit_restaurant} <ArrowRight size={18} /></>
          : !photo && answeredRequired === requiredQuestions.length ? <>{t.add_photo_to_join}</>
          : !photo ? <>{t.answer_and_add_photo(requiredQuestions.length - answeredRequired)}</>
          : <>{t.answer_n_questions(requiredQuestions.length - answeredRequired)}</>}
      </button>

      <div className="mjds-footer">{t.footer_restaurant}</div>
    </Page>
  );
}
